import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Mail, MapPin, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';

const content = {
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last Updated: April 2026",
    intro: {
      title: "Introduction",
      text: "WeDo HR Services UG (\"WeDo\", \"we\", \"us\", or \"our\") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website, services, or interact with us in any way. We comply with the General Data Protection Regulation (GDPR) and applicable German data protection laws."
    },
    controller: {
      title: "Data Controller",
      text: "The data controller responsible for your personal data is:",
      company: "WeDo HR Services UG",
      address: "Düsseldorf, Germany",
      email: "info@wedohr.net",
      phone: "+49 1590 6453419"
    },
    dataCollected: {
      title: "Personal Data We Collect",
      intro: "We may collect and process the following categories of personal data:",
      categories: [
        {
          name: "Contact Information",
          items: ["Name, email address, phone number", "Company name and job title", "Postal address"]
        },
        {
          name: "Job Application Data",
          items: ["CV/Resume and cover letter", "Education and work history", "Professional qualifications and certifications", "References (if provided)", "Skills and competencies"]
        },
        {
          name: "Training & Enrollment Data",
          items: ["Course enrollment information", "Training preferences and requirements", "Professional development history", "Certificates and completion records"]
        },
        {
          name: "Technical Data",
          items: ["IP address and browser type", "Device information", "Website usage data", "Cookies and similar technologies"]
        },
        {
          name: "Communication Data",
          items: ["Correspondence and inquiries", "Feedback and survey responses", "Service requests and preferences"]
        }
      ]
    },
    purposes: {
      title: "How We Use Your Data",
      intro: "We process your personal data for the following purposes:",
      items: [
        {
          purpose: "Service Delivery",
          description: "To provide recruitment, training, HR consulting, and career advisory services you have requested."
        },
        {
          purpose: "Recruitment Services",
          description: "To match candidates with suitable job opportunities, assess qualifications, and facilitate the recruitment process on behalf of our clients."
        },
        {
          purpose: "Training Administration",
          description: "To process course enrollments, manage training sessions, issue certificates, and track professional development."
        },
        {
          purpose: "Communication",
          description: "To respond to your inquiries, provide customer support, and send relevant service updates."
        },
        {
          purpose: "Legal Compliance",
          description: "To comply with legal obligations, including tax and employment regulations."
        },
        {
          purpose: "Website Improvement",
          description: "To analyze website usage and improve our online services and user experience."
        }
      ]
    },
    legalBasis: {
      title: "Legal Basis for Processing",
      intro: "Under the GDPR, we process your personal data based on the following legal grounds:",
      items: [
        {
          basis: "Consent (Art. 6(1)(a) GDPR)",
          description: "Where you have given explicit consent for specific processing activities, such as receiving marketing communications."
        },
        {
          basis: "Contract Performance (Art. 6(1)(b) GDPR)",
          description: "Where processing is necessary to fulfill a contract with you or take pre-contractual steps at your request."
        },
        {
          basis: "Legal Obligation (Art. 6(1)(c) GDPR)",
          description: "Where we are required to process data to comply with legal requirements."
        },
        {
          basis: "Legitimate Interests (Art. 6(1)(f) GDPR)",
          description: "Where processing is necessary for our legitimate business interests, provided these do not override your fundamental rights."
        }
      ]
    },
    dataSharing: {
      title: "Data Sharing and Recipients",
      intro: "We may share your personal data with:",
      items: [
        "Client companies (for recruitment purposes, with your consent)",
        "Training partners and accreditation bodies",
        "IT service providers and hosting services",
        "Professional advisors (legal, accounting)",
        "Regulatory authorities when required by law"
      ],
      note: "We do not sell your personal data to third parties. Any data sharing is conducted in compliance with GDPR requirements and appropriate safeguards."
    },
    retention: {
      title: "Data Retention",
      text: "We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by law. Specific retention periods include:",
      items: [
        "Job application data: Up to 6 months after the recruitment process ends, unless you consent to longer retention for future opportunities",
        "Training records: For the duration required by accreditation bodies and applicable regulations",
        "Contract and billing data: As required by German tax and commercial law (typically 10 years)",
        "Website analytics: Up to 26 months"
      ]
    },
    rights: {
      title: "Your Rights Under GDPR",
      intro: "You have the following rights regarding your personal data:",
      items: [
        {
          right: "Right of Access",
          description: "Request a copy of your personal data we hold."
        },
        {
          right: "Right to Rectification",
          description: "Request correction of inaccurate or incomplete data."
        },
        {
          right: "Right to Erasure",
          description: "Request deletion of your data under certain circumstances."
        },
        {
          right: "Right to Restriction",
          description: "Request limitation of processing in specific situations."
        },
        {
          right: "Right to Data Portability",
          description: "Receive your data in a structured, machine-readable format."
        },
        {
          right: "Right to Object",
          description: "Object to processing based on legitimate interests or for direct marketing."
        },
        {
          right: "Right to Withdraw Consent",
          description: "Withdraw consent at any time where processing is based on consent."
        }
      ],
      exercise: "To exercise any of these rights, please contact us using the details provided below. We will respond to your request within one month as required by the GDPR."
    },
    security: {
      title: "Data Security",
      text: "We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include encrypted data transmission, secure servers, access controls, and regular security assessments."
    },
    international: {
      title: "International Data Transfers",
      text: "Your data is primarily processed within the European Economic Area (EEA). If we transfer data outside the EEA, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses approved by the European Commission."
    },
    cookies: {
      title: "Cookies",
      text: "Our website uses cookies and similar technologies. For detailed information about the cookies we use and how to manage them, please see our Cookie Policy."
    },
    changes: {
      title: "Changes to This Policy",
      text: "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically."
    },
    contact: {
      title: "Contact Us",
      intro: "If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us:",
      complaint: "You also have the right to lodge a complaint with a supervisory authority, in particular in the EU Member State of your residence, place of work, or place of the alleged infringement. In Germany, the relevant authority is the data protection authority of the federal state where we are located."
    }
  },
  ar: {
    title: "سياسة الخصوصية",
    lastUpdated: "آخر تحديث: أبريل 2026",
    intro: {
      title: "مقدمة",
      text: "تلتزم شركة ويدو لخدمات الموارد البشرية (\"ويدو\"، \"نحن\"، \"لنا\") بحماية خصوصيتك وبياناتك الشخصية. توضح سياسة الخصوصية هذه كيفية جمعنا واستخدامنا وتخزيننا وحمايتنا لمعلوماتك الشخصية عند استخدامك لموقعنا الإلكتروني أو خدماتنا أو تفاعلك معنا بأي شكل. نحن نلتزم باللائحة العامة لحماية البيانات (GDPR) وقوانين حماية البيانات الألمانية المعمول بها."
    },
    controller: {
      title: "مراقب البيانات",
      text: "مراقب البيانات المسؤول عن بياناتك الشخصية هو:",
      company: "ويدو لخدمات الموارد البشرية",
      address: "دوسلدورف، ألمانيا",
      email: "info@wedohr.net",
      phone: "+49 1590 6453419"
    },
    dataCollected: {
      title: "البيانات الشخصية التي نجمعها",
      intro: "قد نقوم بجمع ومعالجة الفئات التالية من البيانات الشخصية:",
      categories: [
        {
          name: "معلومات الاتصال",
          items: ["الاسم والبريد الإلكتروني ورقم الهاتف", "اسم الشركة والمسمى الوظيفي", "العنوان البريدي"]
        },
        {
          name: "بيانات طلب التوظيف",
          items: ["السيرة الذاتية وخطاب التقديم", "التاريخ التعليمي والمهني", "المؤهلات والشهادات المهنية", "المراجع (إن وجدت)", "المهارات والكفاءات"]
        },
        {
          name: "بيانات التدريب والتسجيل",
          items: ["معلومات التسجيل في الدورات", "تفضيلات ومتطلبات التدريب", "تاريخ التطوير المهني", "الشهادات وسجلات الإكمال"]
        },
        {
          name: "البيانات التقنية",
          items: ["عنوان IP ونوع المتصفح", "معلومات الجهاز", "بيانات استخدام الموقع", "ملفات تعريف الارتباط والتقنيات المماثلة"]
        },
        {
          name: "بيانات الاتصالات",
          items: ["المراسلات والاستفسارات", "التعليقات وردود الاستبيانات", "طلبات الخدمة والتفضيلات"]
        }
      ]
    },
    purposes: {
      title: "كيف نستخدم بياناتك",
      intro: "نعالج بياناتك الشخصية للأغراض التالية:",
      items: [
        {
          purpose: "تقديم الخدمات",
          description: "لتقديم خدمات التوظيف والتدريب واستشارات الموارد البشرية والإرشاد المهني التي طلبتها."
        },
        {
          purpose: "خدمات التوظيف",
          description: "لمطابقة المرشحين مع فرص العمل المناسبة وتقييم المؤهلات وتسهيل عملية التوظيف نيابة عن عملائنا."
        },
        {
          purpose: "إدارة التدريب",
          description: "لمعالجة التسجيل في الدورات وإدارة جلسات التدريب وإصدار الشهادات وتتبع التطوير المهني."
        },
        {
          purpose: "التواصل",
          description: "للرد على استفساراتك وتقديم الدعم وإرسال تحديثات الخدمة ذات الصلة."
        },
        {
          purpose: "الامتثال القانوني",
          description: "للامتثال للالتزامات القانونية، بما في ذلك اللوائح الضريبية والتوظيفية."
        },
        {
          purpose: "تحسين الموقع",
          description: "لتحليل استخدام الموقع وتحسين خدماتنا عبر الإنترنت وتجربة المستخدم."
        }
      ]
    },
    legalBasis: {
      title: "الأساس القانوني للمعالجة",
      intro: "بموجب اللائحة العامة لحماية البيانات، نعالج بياناتك الشخصية بناءً على الأسس القانونية التالية:",
      items: [
        {
          basis: "الموافقة (المادة 6(1)(أ))",
          description: "عندما تعطي موافقة صريحة لأنشطة معالجة محددة، مثل تلقي الرسائل التسويقية."
        },
        {
          basis: "تنفيذ العقد (المادة 6(1)(ب))",
          description: "عندما تكون المعالجة ضرورية لتنفيذ عقد معك أو اتخاذ خطوات ما قبل التعاقد بناءً على طلبك."
        },
        {
          basis: "الالتزام القانوني (المادة 6(1)(ج))",
          description: "عندما يُطلب منا معالجة البيانات للامتثال للمتطلبات القانونية."
        },
        {
          basis: "المصالح المشروعة (المادة 6(1)(و))",
          description: "عندما تكون المعالجة ضرورية لمصالحنا التجارية المشروعة، شريطة ألا تتجاوز حقوقك الأساسية."
        }
      ]
    },
    dataSharing: {
      title: "مشاركة البيانات والمستلمون",
      intro: "قد نشارك بياناتك الشخصية مع:",
      items: [
        "الشركات العميلة (لأغراض التوظيف، بموافقتك)",
        "شركاء التدريب وهيئات الاعتماد",
        "مزودي خدمات تكنولوجيا المعلومات والاستضافة",
        "المستشارين المهنيين (قانونيين، محاسبين)",
        "السلطات التنظيمية عند الحاجة قانونياً"
      ],
      note: "نحن لا نبيع بياناتك الشخصية لأطراف ثالثة. تتم أي مشاركة للبيانات وفقاً لمتطلبات اللائحة العامة لحماية البيانات والضمانات المناسبة."
    },
    retention: {
      title: "الاحتفاظ بالبيانات",
      text: "نحتفظ ببياناتك الشخصية فقط طالما كان ذلك ضرورياً لتحقيق الأغراض التي جُمعت من أجلها، أو كما يقتضيه القانون. تشمل فترات الاحتفاظ المحددة:",
      items: [
        "بيانات طلب التوظيف: حتى 6 أشهر بعد انتهاء عملية التوظيف، ما لم توافق على الاحتفاظ لفترة أطول للفرص المستقبلية",
        "سجلات التدريب: للمدة المطلوبة من قبل هيئات الاعتماد واللوائح المعمول بها",
        "بيانات العقود والفواتير: حسب ما يقتضيه قانون الضرائب والتجارة الألماني (عادة 10 سنوات)",
        "تحليلات الموقع: حتى 26 شهراً"
      ]
    },
    rights: {
      title: "حقوقك بموجب اللائحة العامة لحماية البيانات",
      intro: "لديك الحقوق التالية فيما يتعلق ببياناتك الشخصية:",
      items: [
        {
          right: "حق الوصول",
          description: "طلب نسخة من بياناتك الشخصية التي نحتفظ بها."
        },
        {
          right: "حق التصحيح",
          description: "طلب تصحيح البيانات غير الدقيقة أو غير المكتملة."
        },
        {
          right: "حق المحو",
          description: "طلب حذف بياناتك في ظروف معينة."
        },
        {
          right: "حق التقييد",
          description: "طلب تقييد المعالجة في حالات محددة."
        },
        {
          right: "حق نقل البيانات",
          description: "تلقي بياناتك بتنسيق منظم وقابل للقراءة آلياً."
        },
        {
          right: "حق الاعتراض",
          description: "الاعتراض على المعالجة القائمة على المصالح المشروعة أو للتسويق المباشر."
        },
        {
          right: "حق سحب الموافقة",
          description: "سحب الموافقة في أي وقت عندما تكون المعالجة مبنية على الموافقة."
        }
      ],
      exercise: "لممارسة أي من هذه الحقوق، يرجى الاتصال بنا باستخدام التفاصيل المقدمة أدناه. سنرد على طلبك خلال شهر واحد كما هو مطلوب بموجب اللائحة العامة لحماية البيانات."
    },
    security: {
      title: "أمان البيانات",
      text: "ننفذ تدابير تقنية وتنظيمية مناسبة لحماية بياناتك الشخصية من الوصول غير المصرح به أو التعديل أو الإفصاح أو الإتلاف. تشمل هذه التدابير نقل البيانات المشفرة والخوادم الآمنة وضوابط الوصول والتقييمات الأمنية المنتظمة."
    },
    international: {
      title: "نقل البيانات الدولي",
      text: "تتم معالجة بياناتك بشكل أساسي داخل المنطقة الاقتصادية الأوروبية. إذا قمنا بنقل البيانات خارج المنطقة الاقتصادية الأوروبية، نضمن وجود ضمانات مناسبة، مثل البنود التعاقدية القياسية المعتمدة من المفوضية الأوروبية."
    },
    cookies: {
      title: "ملفات تعريف الارتباط",
      text: "يستخدم موقعنا الإلكتروني ملفات تعريف الارتباط والتقنيات المماثلة. للحصول على معلومات مفصلة حول ملفات تعريف الارتباط التي نستخدمها وكيفية إدارتها، يرجى الاطلاع على سياسة ملفات تعريف الارتباط الخاصة بنا."
    },
    changes: {
      title: "التغييرات على هذه السياسة",
      text: "قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم نشر أي تغييرات على هذه الصفحة مع تاريخ المراجعة المحدث. نشجعك على مراجعة هذه السياسة بشكل دوري."
    },
    contact: {
      title: "اتصل بنا",
      intro: "إذا كانت لديك أي أسئلة حول سياسة الخصوصية هذه أو ترغب في ممارسة حقوق حماية البيانات الخاصة بك، يرجى الاتصال بنا:",
      complaint: "لديك أيضاً الحق في تقديم شكوى إلى سلطة إشرافية، خاصة في الدولة العضو في الاتحاد الأوروبي التي تقيم فيها أو مكان عملك أو مكان الانتهاك المزعوم. في ألمانيا، السلطة المختصة هي هيئة حماية البيانات في الولاية الفيدرالية التي نتواجد فيها."
    }
  }
};

export const PrivacyPolicyPage = () => {
  const { language, direction } = useLanguage();
  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" dir={direction} data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy"
        titleAr="سياسة الخصوصية"
        description="WeDo HR Services privacy policy. Learn how we collect, use, and protect your personal data in compliance with GDPR and German data protection laws."
        descriptionAr="سياسة خصوصية ويدو لخدمات الموارد البشرية. تعرف على كيفية جمع واستخدام وحماية بياناتك الشخصية وفقًا للائحة العامة لحماية البيانات وقوانين حماية البيانات الألمانية."
        canonicalUrl="/privacy"
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center">
              <Shield className="h-7 w-7 text-[#33AADD]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#111827]">{t.title}</h1>
              <p className="text-sm text-[#6B7280] mt-1">{t.lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="space-y-8">
            {/* Introduction */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.intro.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.intro.text}</p>
              </CardContent>
            </Card>

            {/* Data Controller */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.controller.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.controller.text}</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2">
                  <p className="font-medium text-[#111827]">{t.controller.company}</p>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <MapPin className="h-4 w-4" />
                    <span>{t.controller.address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${t.controller.email}`} className="text-[#33AADD] hover:underline">{t.controller.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${t.controller.phone}`} className="text-[#33AADD] hover:underline">{t.controller.phone}</a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Collected */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.dataCollected.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.dataCollected.intro}</p>
                <div className="space-y-4">
                  {t.dataCollected.categories.map((category, index) => (
                    <div key={index} className="border-s-4 border-[#33AADD]/30 ps-4">
                      <h3 className="font-medium text-[#111827] mb-2">{category.name}</h3>
                      <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                        {category.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Purposes */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.purposes.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.purposes.intro}</p>
                <div className="space-y-4">
                  {t.purposes.items.map((item, index) => (
                    <div key={index} className="bg-[#F9FAFB] rounded-lg p-4">
                      <h3 className="font-medium text-[#111827] mb-1">{item.purpose}</h3>
                      <p className="text-sm text-[#6B7280]">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Legal Basis */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.legalBasis.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.legalBasis.intro}</p>
                <div className="space-y-4">
                  {t.legalBasis.items.map((item, index) => (
                    <div key={index} className="border-s-4 border-[#33AADD]/30 ps-4">
                      <h3 className="font-medium text-[#111827] mb-1">{item.basis}</h3>
                      <p className="text-sm text-[#6B7280]">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.dataSharing.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.dataSharing.intro}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280] mb-4">
                  {t.dataSharing.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-[#6B7280] italic">{t.dataSharing.note}</p>
              </CardContent>
            </Card>

            {/* Retention */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.retention.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.retention.text}</p>
                <ul className="list-disc list-inside space-y-2 text-[#6B7280]">
                  {t.retention.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Rights */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.rights.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.rights.intro}</p>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  {t.rights.items.map((item, index) => (
                    <div key={index} className="bg-[#F9FAFB] rounded-lg p-4">
                      <h3 className="font-medium text-[#111827] mb-1">{item.right}</h3>
                      <p className="text-sm text-[#6B7280]">{item.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[#6B7280]">{t.rights.exercise}</p>
              </CardContent>
            </Card>

            {/* Security */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.security.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.security.text}</p>
              </CardContent>
            </Card>

            {/* International Transfers */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.international.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.international.text}</p>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.cookies.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.cookies.text}</p>
              </CardContent>
            </Card>

            {/* Changes */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.changes.title}</h2>
                <p className="text-[#6B7280] leading-relaxed">{t.changes.text}</p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-[#111827] mb-3">{t.contact.title}</h2>
                <p className="text-[#6B7280] mb-4">{t.contact.intro}</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2 mb-4">
                  <p className="font-medium text-[#111827]">{t.controller.company}</p>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${t.controller.email}`} className="text-[#33AADD] hover:underline">{t.controller.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${t.controller.phone}`} className="text-[#33AADD] hover:underline">{t.controller.phone}</a>
                  </div>
                </div>
                <p className="text-sm text-[#6B7280]">{t.contact.complaint}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
